import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import userService from '../../services/userService';

interface ChangeEmailModalProps {
  open: boolean;
  onClose: () => void;
  currentEmail: string;
  onSuccess: (newEmail: string) => void;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  open,
  onClose,
  currentEmail,
  onSuccess,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleReset = () => {
    setCurrentPassword('');
    setNewEmail('');
    setShowPassword(false);
    setError('');
    setShowConfirmDialog(false);
  };

  // モーダルが閉じられた時に状態をリセット
  useEffect(() => {
    if (!open) {
      handleReset();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setError('');

    // バリデーション
    if (!currentPassword) {
      setError('現在のパスワードを入力してください');
      return;
    }

    if (!newEmail) {
      setError('新しいメールアドレスを入力してください');
      return;
    }

    if (!validateEmail(newEmail)) {
      setError('有効なメールアドレスを入力してください');
      return;
    }

    if (currentEmail === newEmail) {
      setError('現在のメールアドレスと同じです');
      return;
    }

    // 確認ダイアログを表示
    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await userService.changeEmail({
        currentPassword,
        newEmail,
      });

      // 成功メッセージ
      onSuccess(response.data.newEmail);
      handleClose();
    } catch (err: any) {
      console.error('メールアドレス変更エラー:', err);
      setError(err.message || 'メールアドレスの変更に失敗しました');
      setShowConfirmDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
  };

  if (showConfirmDialog) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>メールアドレス変更の確認</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            本当にメールアドレスを変更しますか？
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>現在:</strong> {currentEmail}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>新規:</strong> {newEmail}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              この操作は取り消せません。変更後は新しいメールアドレスでログインしてください。
            </Typography>
          </Alert>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm} disabled={loading}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? '変更中...' : '変更する'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>メールアドレス変更</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="現在のパスワード"
            type={showPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="新しいメールアドレス"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            margin="normal"
            required
            placeholder="example@company.com"
          />

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ 注意事項
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li>変更後は新しいメールアドレスでログインしてください</li>
                <li>この操作は取り消せません</li>
              </ul>
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !currentPassword || !newEmail}
        >
          次へ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeEmailModal;

